import { useSchemaOrg } from '@unhead/schema-org/react';

/**
 * @type {import('$types/components/partials/articles').ArticleSchema}
 */

const ArticleSchema = ({ page, url }) => {
    if (!page) {
        useSchemaOrg([], { tagPosition: 'head' });
        return null;
    }

    const schemas = [];

    // NewsArticle Schema
    const articleSchema = {
        '@type': 'NewsArticle',
        headline: page.title ? page.title.substring(0, 110) : '',
        datePublished: page.publicationDate || page.firstPublishedAt,
    };

    // Author
    if (page.authorName) {
        articleSchema.author = {
            '@type': 'Person',
            name: page.authorName
        };

        // Add additional author details if available
        if (page.authorRole) {
            articleSchema.author.jobTitle = page.authorRole;
        }
        if (page.authorOffice) {
            articleSchema.author.affiliation = {
                '@type': 'Organization',
                name: page.authorOffice
            };
        }
    } else {
        // Default to organization if no author
        articleSchema.author = {
            '@type': 'Organization',
            name: 'Beauchamp Estates'
        };
    }

    // Publisher (RealEstateAgent)
    articleSchema.publisher = {
        '@type': 'RealEstateAgent',
        name: 'Beauchamp Estates',
        url: url?.split('/').slice(0, 3).join('/')
    };

    // Description
    if (page.searchDescription) {
        articleSchema.description = page.searchDescription;
    } else if (page.standfirst) {
        articleSchema.description = page.standfirst;
    } else if (page.listingText) {
        articleSchema.description = page.listingText;
    }

    // Main image - at least 1200px wide for Google News
    const mainImage = page.listingImage?.src
        ? page.listingImage
        : page.images?.[0]?.image?.image;

    if (mainImage?.src) {
        articleSchema.image = {
            '@type': 'ImageObject',
            url: mainImage.src,
            contentUrl: mainImage.src,
        };

        if (mainImage.width) {
            articleSchema.image.width = mainImage.width;
        }

        if (mainImage.height) {
            articleSchema.image.height = mainImage.height;
        }

        if (mainImage.alt) {
            articleSchema.image.caption = mainImage.alt;
        }
    }

    // Date modified
    if (page.lastPublishedAt) {
        articleSchema.dateModified = page.lastPublishedAt;
    } else if (page.lastModified) {
        articleSchema.dateModified = page.lastModified;
    }

    // Main entity of page (canonical URL)
    if (page.canonicalUrl) {
        articleSchema.mainEntityOfPage = {
            '@type': 'WebPage',
            '@id': page.canonicalUrl
        };
    } else if (url) {
        articleSchema.mainEntityOfPage = {
            '@type': 'WebPage',
            '@id': url
        };
    }

    // Article body - extract text from modules
    if (page.modules && page.modules.length > 0) {
        const textContent = page.modules
            .filter(module => module.__typename === 'ArticleTextImageBlock' && module.articleText)
            .map(module => {
                // Strip HTML tags
                const text = module.articleText.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                return text;
            })
            .join(' ');

        if (textContent) {
            articleSchema.articleBody = textContent.substring(0, 5000); // Limit to reasonable length
        }
    }

    // Breadcrumb
    if (page.parent) {
        articleSchema.breadcrumb = {
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: url?.split('/').slice(0, 3).join('/')
                }
            ]
        };

        if (page.parent.title) {
            articleSchema.breadcrumb.itemListElement.push({
                '@type': 'ListItem',
                position: 2,
                name: page.parent.title,
                item: page.parent.relativeUrl
                    ? `${url?.split('/').slice(0, 3).join('/')}${page.parent.relativeUrl}`
                    : url?.split('/').slice(0, 3).join('/')
            });
        }

        articleSchema.breadcrumb.itemListElement.push({
            '@type': 'ListItem',
            position: articleSchema.breadcrumb.itemListElement.length + 1,
            name: page.title,
            item: url
        });
    }

    schemas.push(articleSchema);

    useSchemaOrg(schemas, { tagPosition: 'head' });

    return null;
};

export default ArticleSchema;
